import { useEffect, useMemo, useState } from 'react'
import ApiUsers from '../../../api/ApiUsers'
import ApiAdminTools from '../../../api/ApiAdminTools'
import toastr from 'toastr'
import {
  generateTempPassword,
  getAdminUsersStats,
  getFilteredAndSortedUsers,
  getUserEditUpdates,
  isEmailVerified,
  validateEditFormData,
} from './adminUsersUtils'

const defaultEditFormData = {
  email: '',
  firstname: '',
  lastname: '',
  user_type: 'player',
  email_verified: false,
}

export default function useAdminUsersData({ searchParams }) {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showResetModal, setShowResetModal] = useState(false)
  const [resetUser, setResetUser] = useState(null)
  const [tempPassword, setTempPassword] = useState('')
  const [impersonating, setImpersonating] = useState(false)
  const [showMakeAdminModal, setShowMakeAdminModal] = useState(false)
  const [userToMakeAdmin, setUserToMakeAdmin] = useState(null)
  const [showImpersonateModal, setShowImpersonateModal] = useState(false)
  const [impersonateTarget, setImpersonateTarget] = useState(null)
  const [impersonateReason, setImpersonateReason] = useState('')
  const [editingUserId, setEditingUserId] = useState(null)
  const [editFormData, setEditFormData] = useState(defaultEditFormData)
  const [editErrors, setEditErrors] = useState({})
  const [isSaving, setIsSaving] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortColumn, setSortColumn] = useState('created_at')
  const [sortDirection, setSortDirection] = useState('desc')
  const [activeFilter, setActiveFilter] = useState('all')

  useEffect(() => {
    async function fetchUsers() {
      try {
        const data = await ApiAdminTools.getUsersWithRegistrationStats()
        setUsers(data)
      } catch (err) {
        setError(err.message || 'Error fetching users')
      } finally {
        setLoading(false)
      }
    }

    fetchUsers()
  }, [])

  useEffect(() => {
    const targetUserIdRaw = searchParams.get('userId')
    const targetUserId = parseInt(targetUserIdRaw, 10)
    const timeoutIds = []

    if (
      targetUserIdRaw &&
      Number.isInteger(targetUserId) &&
      targetUserId > 0 &&
      users.length > 0
    ) {
      const targetUser = users.find((u) => u.id === targetUserId)
      if (targetUser) {
        const scrollTimeout = setTimeout(() => {
          const targetElement = document.getElementById(
            `user-row-${targetUserId}`,
          )
          if (targetElement) {
            targetElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            })
            if (targetElement.tagName === 'TR') {
              targetElement.classList.add('table-warning')
              const removeTimeout = setTimeout(() => {
                targetElement.classList.remove('table-warning')
              }, 3000)
              timeoutIds.push(removeTimeout)
            } else {
              targetElement.classList.add('bg-warning-subtle')
              const removeTimeout = setTimeout(() => {
                targetElement.classList.remove('bg-warning-subtle')
              }, 3000)
              timeoutIds.push(removeTimeout)
            }
          }
        }, 100)

        timeoutIds.push(scrollTimeout)
      }
    }

    return () => {
      timeoutIds.forEach((id) => clearTimeout(id))
    }
  }, [searchParams, users])

  const filteredAndSortedUsers = useMemo(
    () =>
      getFilteredAndSortedUsers({
        users,
        searchTerm,
        sortColumn,
        sortDirection,
        activeFilter,
      }),
    [users, searchTerm, sortColumn, sortDirection, activeFilter],
  )

  const stats = useMemo(
    () => getAdminUsersStats(users, filteredAndSortedUsers.length),
    [users, filteredAndSortedUsers],
  )

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return
    try {
      await ApiUsers.remove(userId)
      setUsers((currentUsers) => currentUsers.filter((u) => u.id !== userId))
    } catch (err) {
      setError(err.message)
    }
  }

  const handleMakeAdmin = (userId) => {
    const targetUser = users.find((u) => u.id === userId)
    setUserToMakeAdmin(targetUser)
    setShowMakeAdminModal(true)
  }

  const handleConfirmMakeAdmin = async () => {
    if (!userToMakeAdmin) return

    try {
      await ApiUsers.update({ id: userToMakeAdmin.id, user_type: 'admin' })
      setUsers((currentUsers) =>
        currentUsers.map((u) =>
          u.id === userToMakeAdmin.id ? { ...u, user_type: 'admin' } : u,
        ),
      )
      setShowMakeAdminModal(false)
      setUserToMakeAdmin(null)
      toastr.success(
        `${userToMakeAdmin.email} has been promoted to admin`,
        'Admin Promoted',
      )
    } catch (err) {
      setError(err.message)
      setShowMakeAdminModal(false)
      setUserToMakeAdmin(null)
    }
  }

  const handleCloseMakeAdminModal = () => {
    setShowMakeAdminModal(false)
    setUserToMakeAdmin(null)
  }

  const handleResetPasswordClick = (userRow) => {
    setResetUser(userRow)
    setTempPassword('')
    setShowResetModal(true)
  }

  const handleGeneratePassword = async () => {
    const pwd = generateTempPassword()
    setTempPassword(pwd)
    if (resetUser && resetUser.id) {
      try {
        await ApiUsers.adminSetPassword({
          user_id: resetUser.id,
          new_password: pwd,
        })
        toastr.success('Temporary password set for user!')
      } catch (err) {
        toastr.error(err.message || 'Failed to set password')
      }
    }
  }

  const handleImpersonate = (targetUser) => {
    setImpersonateTarget(targetUser)
    setImpersonateReason('')
    setShowImpersonateModal(true)
  }

  const handleConfirmImpersonate = async () => {
    if (!impersonateTarget) return

    if (!impersonateReason || impersonateReason.trim().length === 0) {
      toastr.error('Please provide a reason for impersonation')
      return
    }

    try {
      setImpersonating(true)
      await ApiAdminTools.impersonateUser(
        impersonateTarget.id,
        impersonateReason.trim(),
      )
      setShowImpersonateModal(false)
      toastr.success(
        `Now logged in as ${impersonateTarget.email}. Redirecting to home...`,
      )
      setTimeout(() => {
        window.location.assign('/')
      }, 600)
    } catch (err) {
      setImpersonating(false)
      toastr.error(err.message || 'Failed to impersonate user')
    }
  }

  const handleCloseImpersonateModal = () => {
    setShowImpersonateModal(false)
    setImpersonateTarget(null)
    setImpersonateReason('')
  }

  const handleCloseResetModal = () => {
    setShowResetModal(false)
    setResetUser(null)
    setTempPassword('')
  }

  const handleStartEdit = (userRow) => {
    setEditingUserId(userRow.id)
    setEditFormData({
      email: userRow.email || '',
      firstname: userRow.firstname || '',
      lastname: userRow.lastname || '',
      user_type: userRow.user_type || 'player',
      email_verified: isEmailVerified(userRow),
    })
    setEditErrors({})
  }

  const handleCancelEdit = () => {
    setEditingUserId(null)
    setEditFormData(defaultEditFormData)
    setEditErrors({})
  }

  const handleSaveEdit = async () => {
    const errors = validateEditFormData(editFormData)
    setEditErrors(errors)
    if (Object.keys(errors).length > 0) return

    const userRow = users.find((u) => u.id === editingUserId)
    if (!userRow) return

    const emailChanged =
      editFormData.email.toLowerCase() !== userRow.email.toLowerCase()
    const userTypeChanged = editFormData.user_type !== userRow.user_type

    if (emailChanged || userTypeChanged) {
      let confirmMessage = 'You are about to make critical changes:\n\n'
      if (emailChanged) {
        confirmMessage += `• Email: ${userRow.email} → ${editFormData.email}\n`
      }
      if (userTypeChanged) {
        confirmMessage += `• User Type: ${userRow.user_type} → ${editFormData.user_type}\n`
      }
      confirmMessage += '\nAre you sure you want to continue?'

      if (!window.confirm(confirmMessage)) {
        return
      }
    }

    setIsSaving(true)

    try {
      const updates = getUserEditUpdates(editFormData, userRow)
      const result = await ApiAdminTools.updateUser(editingUserId, updates)

      if (result.success && result.user) {
        setUsers((currentUsers) =>
          currentUsers.map((u) => (u.id === editingUserId ? result.user : u)),
        )
        toastr.success('User updated successfully')
        handleCancelEdit()
      }
    } catch (err) {
      toastr.error(err.message || 'Failed to update user')
      setEditErrors({ general: err.message })
    } finally {
      setIsSaving(false)
    }
  }

  return {
    loading,
    error,
    filteredAndSortedUsers,
    stats,
    searchTerm,
    setSearchTerm,
    activeFilter,
    setActiveFilter,
    sortColumn,
    sortDirection,
    editingUserId,
    editFormData,
    setEditFormData,
    editErrors,
    isSaving,
    impersonating,
    showResetModal,
    resetUser,
    tempPassword,
    showMakeAdminModal,
    userToMakeAdmin,
    showImpersonateModal,
    impersonateTarget,
    impersonateReason,
    setImpersonateReason,
    handleSort,
    handleDelete,
    handleMakeAdmin,
    handleConfirmMakeAdmin,
    handleCloseMakeAdminModal,
    handleResetPasswordClick,
    handleGeneratePassword,
    handleImpersonate,
    handleConfirmImpersonate,
    handleCloseImpersonateModal,
    handleCloseResetModal,
    handleStartEdit,
    handleCancelEdit,
    handleSaveEdit,
  }
}
